<template>
    <NuxtLayout>
        <div class="mx-auto flex max-w-5xl flex-col items-center gap-10 md:flex-row md:items-start md:justify-between">
            <div class="max-w-xl">
                <p class="text-sm font-semibold text-primary-600 dark:text-primary-400">Chyba {{ statusCode }}</p>
                <h1 class="mt-2 text-3xl font-bold text-gray-900 dark:text-txt-headings md:text-4xl">{{ heading }}</h1>
                <p class="mt-4 text-gray-600 dark:text-txt-body">{{ description }}</p>

                <p
                    v-if="devMessage"
                    class="mt-4 rounded-lg bg-red-50 px-4 py-3 font-mono text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"
                >
                    {{ devMessage }}
                </p>

                <div class="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        class="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                        @click="goHome"
                    >
                        Späť na úvodnú stránku
                    </button>
                    <a
                        class="rounded-full px-5 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 dark:text-txt-body dark:hover:text-txt-headings"
                        href="mailto:help@growni.sk"
                    >
                        Nahlásiť problém
                    </a>
                </div>

                <nav aria-labelledby="error-sections-title" class="mt-10">
                    <h2 id="error-sections-title" class="text-sm font-semibold text-gray-900 dark:text-txt-headings">
                        Alebo pokračuj tu
                    </h2>
                    <ul class="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                        <li v-for="section in sections" :key="section.to" class="!mb-0">
                            <NuxtLink
                                :to="section.to"
                                class="flex w-full items-center gap-3 rounded-xl border border-default bg-surface-card p-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-subtle dark:text-txt-body"
                            >
                                <span
                                    class="flex size-9 shrink-0 items-center justify-center rounded-full"
                                    :class="section.circleClass"
                                >
                                    <CIcon v-if="section.icon" :name="section.icon" class="size-5" />
                                    <Icon v-else name="heroicons:book-open" class="size-5" />
                                </span>
                                {{ section.label }}
                            </NuxtLink>
                        </li>
                    </ul>
                </nav>
            </div>

            <!-- The PNG has a white background: rounded, it reads as a card in dark mode -->
            <img alt="" class="hidden w-72 shrink-0 rounded-3xl md:block" src="@/assets/images/404-picture.png" />
        </div>
    </NuxtLayout>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()

// Same icons and colors as the main navigation (MainNavTabs.vue)
const sections = [
    {
        to: '/mentori',
        label: 'Mentoring',
        icon: 'mentoring',
        circleClass: 'bg-[rgba(4,114,116,0.1)] text-[#047274] dark:text-primary-400'
    },
    {
        to: '/dobrobaza',
        label: 'Dobrovoľníctvo',
        icon: 'volunteering',
        circleClass: 'bg-[rgba(234,179,8,0.1)] text-[#eab308]'
    },
    { to: '/praca', label: 'Práca', icon: 'jobs', circleClass: 'bg-[rgba(249,115,22,0.1)] text-[#f97316]' },
    {
        to: '/vzdelavanie',
        label: 'Vzdelávanie',
        icon: 'skills',
        circleClass: 'bg-[rgba(194,76,252,0.1)] text-[#c24cfc]'
    },
    {
        to: '/sebapoznanie',
        label: 'Sebapoznanie',
        icon: 'heart-outline',
        circleClass: 'bg-[rgba(236,72,153,0.1)] text-[#ec4899]'
    },
    // No growni icon for the blog: the nav uses heroicons:book-open
    { to: '/blog', label: 'Blog', circleClass: 'bg-[rgba(59,130,246,0.1)] text-[#3b82f6]' }
]

const statusCode = computed(() => props.error.status ?? props.error.statusCode ?? 500)
const isNotFound = computed(() => statusCode.value === 404)

const heading = computed(() => (isNotFound.value ? 'Stránka sa nenašla' : 'Niečo sa pokazilo'))
const description = computed(() =>
    isNotFound.value
        ? 'Stránka, ktorú hľadáš, neexistuje alebo bola presunutá.'
        : 'Ospravedlňujeme sa, pri načítaní stránky nastala chyba. Skús to o chvíľu znova.'
)

// Only in development: the raw message helps debugging, visitors don't need it.
const devMessage = computed(() => (import.meta.dev && !isNotFound.value ? props.error.message : ''))

useHead({
    title: computed(() => `${heading.value} | GROWNi.sk`),
    meta: [{ name: 'robots', content: 'noindex' }]
})

function goHome() {
    clearError({ redirect: '/' })
}
</script>
